import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import AuthInput from "@/components/auth/AuthInput";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useLocalization } from "@/lib/localization";

function flagFromCountryCode(code) {
  if (!code || code.length !== 2) {
    return null;
  }

  return code
    .toUpperCase()
    .split("")
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join("");
}

export function PhoneNumberField({
  country,
  countries,
  onCountryChange,
  value,
  onChange,
  placeholder,
  autoFocus = false,
  testId = "phone-number-input",
}) {
  const { locale } = useLocalization();
  const [search, setSearch] = useState("");
  const resolvedPlaceholder = placeholder || country.placeholder || "801 234 5678";
  const activeFlag = flagFromCountryCode(country.code);
  const regionNames = typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames([locale], { type: "region" })
    : null;
  const filteredCountries = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return countries;
    return countries.filter((candidate) =>
      `${candidate.name} ${candidate.code} ${candidate.dial} ${candidate.currency}`.toLowerCase().includes(query),
    );
  }, [countries, search]);

  return (
    <div className="flex min-w-0 items-stretch gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={`${testId}-country`}
            className="h-12 px-2.5 inline-flex items-center gap-1.5 rounded-xl border border-primary/45 bg-muted text-sm font-medium shrink-0 outline-none shadow-none transition-colors duration-200 focus:outline-none focus-visible:border-primary/45 focus-visible:bg-muted focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            {activeFlag ? <span className="text-base leading-none">{activeFlag}</span> : null}
            <span className="font-mono tabular-nums">{country.dial}</span>
            <span className="hidden text-[10px] font-semibold text-muted-foreground sm:inline">{country.currency}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[min(16rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-popover p-1 shadow-md outline-none ring-0"
        >
          <input
            aria-label="Search countries"
            className="mb-1 h-10 w-full rounded-lg border bg-background px-3 text-sm outline-none focus:border-primary"
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder="Search country or currency"
            value={search}
          />
          <div className="max-h-72 overflow-y-auto">
          {filteredCountries.map((c) => (
            <DropdownMenuItem
              key={c.code}
              data-testid={`${testId}-country-${c.code}`}
              onClick={() => onCountryChange(c)}
              className="gap-2 rounded-md outline-none focus:bg-accent focus:text-accent-foreground"
            >
              <span className="text-base leading-none">{flagFromCountryCode(c.code) || c.code}</span>
              <span className="flex-1 truncate">{regionNames?.of(c.code) || c.name}</span>
              <span className="font-mono text-xs text-muted-foreground">{c.dial}</span>
              <span className="text-[10px] font-semibold text-muted-foreground">{c.currency}</span>
            </DropdownMenuItem>
          ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <AuthInput
        data-testid={testId}
        type="tel"
        inputMode="numeric"
        autoFocus={autoFocus}
        placeholder={resolvedPlaceholder}
        value={value}
        onChange={onChange}
        className="flex-1 text-base font-mono tabular-nums"
      />
    </div>
  );
}

export default PhoneNumberField;
